import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const emptyReject = () => ({ used_plates: "", date_from: "", date_to: "" });

const VehicleUsageConfirmModal = () => {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [answers, setAnswers] = useState({}); // vehicle_id -> 'confirmed' | 'rejected'
  const [rejects, setRejects] = useState({}); // vehicle_id -> { used_plates, date_from, date_to }
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const role = user?.role || localStorage.getItem("role");
  const isUser = role === "user";
  const publicPaths = ["/login", "/register", "/privacy-policy", "/home", "/unauthorized"];
  const onPublic = publicPaths.some((p) => location.pathname.startsWith(p));

  const loadPending = useCallback(async () => {
    if (!isUser || onPublic || authLoading) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/api/vehicle-usage-logs/pending", authHeaders());
      setPayload(res.data);
      if (res.data?.required && Array.isArray(res.data.vehicles)) {
        const initAnswers = {};
        const initRejects = {};
        res.data.vehicles.forEach((v) => {
          initAnswers[v.vehicle_id] = "";
          initRejects[v.vehicle_id] = emptyReject();
        });
        setAnswers(initAnswers);
        setRejects(initRejects);
      }
    } catch (err) {
      console.error(err);
      // Do not hard-block on API errors (e.g. table not migrated yet)
      setPayload({ required: false });
    } finally {
      setLoading(false);
    }
  }, [isUser, onPublic, authLoading]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const show = Boolean(isUser && !onPublic && !authLoading && payload?.required);

  const setAnswer = (vehicleId, value) => {
    setAnswers((prev) => ({ ...prev, [vehicleId]: value }));
    if (value !== "rejected") {
      setRejects((prev) => ({ ...prev, [vehicleId]: emptyReject() }));
    }
  };

  const setRejectField = (vehicleId, field, value) => {
    setRejects((prev) => ({
      ...prev,
      [vehicleId]: { ...emptyReject(), ...(prev[vehicleId] || {}), [field]: value }
    }));
  };

  const handleSubmit = async () => {
    setError("");
    const vehicles = payload?.vehicles || [];
    for (const v of vehicles) {
      const ans = answers[v.vehicle_id];
      if (ans !== "confirmed" && ans !== "rejected") {
        setError(t("fleet.usageLogAnswerAll"));
        return;
      }
      if (ans === "rejected") {
        const r = rejects[v.vehicle_id] || emptyReject();
        if (!r.used_plates.trim() || !r.date_from || !r.date_to) {
          setError(t("fleet.usageLogRejectRequired"));
          return;
        }
        if (r.date_from > r.date_to) {
          setError(t("fleet.usageLogDateOrder"));
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      for (const v of vehicles) {
        const ans = answers[v.vehicle_id];
        const body = {
          vehicle_id: v.vehicle_id,
          week_number: payload.week_number,
          answer: ans
        };
        if (ans === "rejected") {
          const r = rejects[v.vehicle_id];
          body.used_plates = r.used_plates.trim();
          body.date_from = r.date_from;
          body.date_to = r.date_to;
        }
        await axios.post("/api/vehicle-usage-logs/answer", body, authHeaders());
      }
      setPayload({ required: false });
    } catch (err) {
      setError(err.response?.data?.error || t("fleet.usageLogSaveError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const range = payload.weekRange || {};
  const weekLabel = `${payload.week_number}${
    range.monday && range.sunday ? ` (${range.monday} — ${range.sunday})` : ""
  }`;

  return (
    <Modal show backdrop="static" keyboard={false} centered size="lg">
      <Modal.Header>
        <Modal.Title>{t("fleet.usageLogConfirmTitle")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            <p className="mb-3">
              {t("fleet.usageLogConfirmIntro", { week: weekLabel })}
            </p>
            {error && <Alert variant="danger">{error}</Alert>}
            {(payload.vehicles || []).map((v) => (
              <div key={v.vehicle_id} className="border rounded p-3 mb-3">
                <div className="fw-bold mb-2">{v.label || v.plate}</div>
                <p className="small text-muted mb-2">
                  {t("fleet.usageLogConfirmQuestion", { vehicle: v.label || v.plate })}
                </p>
                <div className="d-flex gap-2 mb-2">
                  <Button
                    size="sm"
                    variant={answers[v.vehicle_id] === "confirmed" ? "success" : "outline-success"}
                    onClick={() => setAnswer(v.vehicle_id, "confirmed")}
                    disabled={submitting}
                  >
                    {t("fleet.usageLogYes")}
                  </Button>
                  <Button
                    size="sm"
                    variant={answers[v.vehicle_id] === "rejected" ? "danger" : "outline-danger"}
                    onClick={() => setAnswer(v.vehicle_id, "rejected")}
                    disabled={submitting}
                  >
                    {t("fleet.usageLogNo")}
                  </Button>
                </div>
                {answers[v.vehicle_id] === "rejected" && (
                  <div className="mt-2">
                    <Form.Group className="mb-2">
                      <Form.Label>{t("fleet.usageLogUsedPlates")}</Form.Label>
                      <Form.Control
                        value={(rejects[v.vehicle_id] || emptyReject()).used_plates}
                        onChange={(e) => setRejectField(v.vehicle_id, "used_plates", e.target.value)}
                        placeholder={t("fleet.usageLogUsedPlatesPlaceholder")}
                        disabled={submitting}
                      />
                    </Form.Group>
                    <div className="row g-2">
                      <div className="col-6">
                        <Form.Label>{t("fleet.usageLogDateFrom")}</Form.Label>
                        <Form.Control
                          type="date"
                          value={(rejects[v.vehicle_id] || emptyReject()).date_from}
                          onChange={(e) => setRejectField(v.vehicle_id, "date_from", e.target.value)}
                          disabled={submitting}
                        />
                      </div>
                      <div className="col-6">
                        <Form.Label>{t("fleet.usageLogDateTo")}</Form.Label>
                        <Form.Control
                          type="date"
                          value={(rejects[v.vehicle_id] || emptyReject()).date_to}
                          onChange={(e) => setRejectField(v.vehicle_id, "date_to", e.target.value)}
                          disabled={submitting}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleSubmit} disabled={submitting || loading}>
          {submitting ? t("fleet.saving") : t("fleet.usageLogSubmit")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VehicleUsageConfirmModal;
